// Tablero común (primer uso: Inventario de activos de Mantenimiento). Lógica pura: búsqueda mientras se escribe,
// filtros combinables (AND entre filtros, OR dentro de un filtro), grupos por un campo con su color y conteo, y la
// configuración que se guarda como filtro del usuario. Sensación de tablero de trabajo, sin fórmulas, automatizaciones,
// Gantt, carga de trabajo ni Kanban (fuera de alcance de este primer uso).

export interface Opcion { valor: string; etiqueta: string }

export type EdicionCelda = { tipo: 'texto'; max?: number } | { tipo: 'opcion'; opciones: Opcion[]; vacio?: string };

export interface ColumnaTablero<T> {
  clave: string;
  titulo: string;
  /** Texto de la celda (se usa para mostrar, buscar y exportar). */
  texto: (f: T) => string;
  /** Edición directa en la celda (solo si el usuario puede editar). Devuelve null si esa fila no se edita aquí. */
  edicion?: (f: T) => EdicionCelda | null;
  /** Valor actual para el editor (por defecto, el texto). */
  valorEdicion?: (f: T) => string;
  /** Ancho relativo en la grilla (fr). */
  ancho?: number;
  /** Se oculta en el celular (la fila pasa a tarjeta). */
  ocultarEnMovil?: boolean;
  /** Se incluye en la búsqueda (por defecto sí). */
  buscable?: boolean;
}

export interface FiltroDef<T> {
  clave: string;
  titulo: string;
  opciones: Opcion[];
  valor: (f: T) => string | string[] | null;
}

export type EstadoFiltros = Record<string, string[]>;

export interface GrupoDef { valor: string; etiqueta: string; color: string }
export interface Agrupacion<T> { clave: string; titulo: string; valor: (f: T) => string; grupos: GrupoDef[] }

export interface ConfigTablero { filtros: EstadoFiltros; busqueda: string; grupo: string }

/** Minúsculas y sin tildes, para buscar "maquina" y encontrar "Máquina". */
export const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function coincideBusqueda<T>(f: T, busqueda: string, columnas: ColumnaTablero<T>[]): boolean {
  const palabras = normalizar(busqueda).split(/\s+/).filter(Boolean);
  if (!palabras.length) return true;
  const texto = normalizar(columnas.filter(c => c.buscable !== false).map(c => c.texto(f)).join(' '));
  return palabras.every(p => texto.includes(p));
}

export function cumpleFiltros<T>(f: T, defs: FiltroDef<T>[], estado: EstadoFiltros): boolean {
  return defs.every(d => {
    const elegidos = estado[d.clave] ?? [];
    if (!elegidos.length) return true;
    const v = d.valor(f);
    const valores = Array.isArray(v) ? v : v === null || v === undefined ? [''] : [v];
    return valores.some(x => elegidos.includes(x));
  });
}

export function aplicarFiltros<T>(filas: T[], defs: FiltroDef<T>[], estado: EstadoFiltros, busqueda: string, columnas: ColumnaTablero<T>[]): T[] {
  return filas.filter(f => cumpleFiltros(f, defs, estado) && coincideBusqueda(f, busqueda, columnas));
}

export interface Grupo<T> { def: GrupoDef; filas: T[] }

/** Grupos en el orden definido (sin los vacíos); lo que no cae en ninguno va a "Sin dato". */
export function agrupar<T>(filas: T[], ag: Agrupacion<T> | null): Grupo<T>[] {
  if (!ag) return [{ def: { valor: '', etiqueta: 'Todos', color: '#64748b' }, filas }];
  const out: Grupo<T>[] = ag.grupos.map(def => ({ def, filas: filas.filter(f => ag.valor(f) === def.valor) }));
  const conocidos = new Set(ag.grupos.map(g => g.valor));
  const resto = filas.filter(f => !conocidos.has(ag.valor(f)));
  if (resto.length) out.push({ def: { valor: '\u0000', etiqueta: 'Sin dato', color: '#64748b' }, filas: resto });
  return out.filter(g => g.filas.length > 0);
}

export const filtrosActivos = (estado: EstadoFiltros) => Object.values(estado).reduce((n, v) => n + (v.length ? 1 : 0), 0);

/** Alterna una opción de un filtro (devuelve un estado nuevo). */
export function alternar(estado: EstadoFiltros, clave: string, valor: string): EstadoFiltros {
  const actual = estado[clave] ?? [];
  const nuevo = actual.includes(valor) ? actual.filter(v => v !== valor) : [...actual, valor];
  return { ...estado, [clave]: nuevo };
}

/** Configuración válida a partir de un filtro guardado (ignora claves que ya no existen). */
export function configDesdeGuardado<T>(c: unknown, defs: FiltroDef<T>[], agrupaciones: Agrupacion<T>[], grupoPorDefecto: string): ConfigTablero {
  const o = (c && typeof c === 'object' ? c : {}) as Partial<ConfigTablero>;
  const filtros: EstadoFiltros = {};
  for (const d of defs) {
    const v = o.filtros?.[d.clave];
    if (Array.isArray(v)) filtros[d.clave] = v.filter(x => typeof x === 'string' && d.opciones.some(op => op.valor === x));
  }
  const grupo = typeof o.grupo === 'string' && (o.grupo === '' || agrupaciones.some(a => a.clave === o.grupo)) ? o.grupo : grupoPorDefecto;
  return { filtros, busqueda: typeof o.busqueda === 'string' ? o.busqueda.slice(0, 120) : '', grupo };
}

/** Selección: marcar o desmarcar todas las visibles. */
export function alternarTodas(seleccion: Set<string>, visibles: string[]): Set<string> {
  const todas = visibles.length > 0 && visibles.every(id => seleccion.has(id));
  const s = new Set(seleccion);
  visibles.forEach(id => (todas ? s.delete(id) : s.add(id)));
  return s;
}
